import { defineStore } from 'pinia';

/** State of the audio module (notification sound playback) */
interface AudioState {
  // Old
  /** Legacy HTMLAudioElement-based player, or `null` when idle */
  audio: HTMLAudioElement | null;

  // New
  /** Web Audio API context, or `null` until initialized */
  audioContext: AudioContext | null;

  /**
   * Holds references to audio nodes, since the JS API has no
   * way of exposing access to nodes connected to the audio context.
   */
  _nodeCache: {
    /** Volume controller node, or `null` until initialized */
    gain: GainNode | null;
    /** Looping silent source node keeping the context alive, or `null` until initialized */
    silent: AudioBufferSourceNode | null;
  };

  /**
   * Cache of basically raw audio bytes.
   * [key: url]: AudioBuffer
   */
  _bufferCache: Record<string, AudioBuffer>;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/audio.ts` (namespace `audio`).
 * In-memory only — the legacy module had no `vuexPersist`/reducer entry, so this store gets no
 * `vuexPersist` option.
 *
 * No getters, mutations, or cross-namespace (`rootGetters`/`dispatch`/`commit` with `{root:true}`)
 * access existed in the legacy module, so there are no renames and no sibling-store dependencies
 * for this conversion. The legacy `mutations` object was empty and is dropped entirely.
 */
export const useAudioStore = defineStore('audio', {
  state: (): AudioState => ({
    // Old
    audio: null,

    // New
    audioContext: null,

    /**
     * Holds references to audio nodes, since the JS API has no
     * way of exposing access to nodes connected to the audio context.
     */
    _nodeCache: {
      gain: null,
      silent: null,
    },

    /**
     * Cache of basically raw audio bytes.
     * [key: url]: AudioBuffer
     */
    _bufferCache: {},
  }),
  actions: {
    playAudioOld({ url, volume }: { url: string; volume?: number }) {
      if (!url) return;

        try {
          this.audio?.pause();
          this.audio = null;
          this.audio = new Audio(url);
          this.audio.volume = volume || 0.5;
          this.audio.play().catch(() => {});
      } catch (e) {}
    },
    initAudioContext() {
      /**
       * The audio system is sort of like a transformation pipeline.
       * You plug nodes together manually to control the flow of audio.
       * A context is the overarching "controller", and holds the user's
       * default sound output (destination)
       *
       * Connections are set up as:
       * - <Sound effect node> -> Volume Controller (gain) -> AudioContext destination
       * - <Silent sound> -> AudioContext destination
       */

      // Set up the "audio player" itself if it doesn't exist
      if (!this.audioContext) {
        // Webkit one is for very old browsers (not in current DOM typings)
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        console.debug("[Audio] Context initialized");
      }

      // If it does exist, always try to resume it if it is suspended, so we can play audio
      // (`status` is kept as-is from the JS original; AudioContext exposes `state` in the spec)
      if ((this.audioContext as any).status === 'suspended') {
        this.audioContext.resume();
        console.debug("[Audio] Context resumed");
      }

      /**
       * Set up a volume controller if one does not exist already.
       * The audio context must be available too, as we set it as the output.
       */
      if (!this._nodeCache.gain && this.audioContext) {
        // Create volume controller
        this._nodeCache.gain = new GainNode(this.audioContext, {
          gain: 0.5, // Default volume
        });

        // Connect the volume to the sound output
        this._nodeCache.gain.connect(this.audioContext.destination);

        console.debug("[Audio] Gain node initialized");
      }

      // Set up a silent audio that plays continuously in the background, to keep the audio context alive
      if (!this._nodeCache.silent && this.audioContext) {
        this.initSilentAudio();
      }
    },
    /**
     * Retrieves the audio buffer, either from cache if available, or from a URL
     * @param url
     * @returns Promise resolving with the decoded AudioBuffer
     */
    async getAudioBuffer({ url }: { url: string }): Promise<AudioBuffer> {
      /**
       * Cache is disabled for now, I don't think it is needed, but we'll see.
       * Reason is that it is a static file, so the browser will be caching the
       * resource for up to a year based on current header settings.
       *
       * The cache is fetched from memory if available, or disk if there,
       * so we shouldn't really need the manual memory-only cache.
       */
      // if (url in this._bufferCache) return this._bufferCache[url];

      return fetch(url)
        .then(r => r.arrayBuffer())
        .then(buffer => this.audioContext!.decodeAudioData(buffer))
        .then(decoded => {
          // this._bufferCache[url] = decoded;
          return decoded;
        });
    },
    async playAudio({ url, volume }: { url: string; volume?: number }) {
      if (!url) return;

      // Ideally it is always initialized before you even reach this, but check just in case
      if (!this.audioContext) {
        await this.initAudioContext();
      }

      this.getAudioBuffer({ url })
        .then((decoded: AudioBuffer) => {
          /**
           * We must create a new AudioNode from the sound to be played every time,
           * since AudioNodes cannot be re-used after having been played.
           */
          const audioNode = new AudioBufferSourceNode(this.audioContext!, {
            buffer: decoded,
          });

          /**
           * Plug the sound node into the volume controller, which is already connected to output/AudioContext.
           * Adjust the volume of the gain controller to the input argument.
           */
          audioNode.connect(this._nodeCache.gain!);
          this._nodeCache.gain!.gain.value = volume || 0.5;

          // Play the sound
          audioNode.start(0);

          /**
           * Disconnect outputs (gain node) of the audio node when it is done,
           * so that it has no references and can be garbage collected
           */
          audioNode.onended = () => audioNode.disconnect();
        })
        .catch(console.error);
    },
    /**
     * Creates audio from scratch that is completely silent.
     * It plays in the background in order to keep the audio context alive, which prevents
     * us from losing access to play sounds from the browser after periods of inactivity.
     * It is a short 3-second mono channel filled with empty sound (0's) that is played
     * in a continuous loop.
     */
    initSilentAudio() {
      // Create a buffer to contain sound, 1 channel, 3 seconds based on sample rate
      const buffer = this.audioContext!.createBuffer(1, this.audioContext!.sampleRate * 3, this.audioContext!.sampleRate);

      // Fill the channel with empty sound
      const channelBuffer = buffer.getChannelData(0);
      for (let i=0;i<channelBuffer.length; i++) channelBuffer[i] = 0;

      // Create a source node that will use this buffer and play in repeat
      const source = this.audioContext!.createBufferSource();
      source.buffer = buffer;
      source.loop = true;

      // Connect the empty sound to the output and then start playing
      source.connect(this.audioContext!.destination);
      source.start(0);

      // Keep it around for cleanup
      this._nodeCache.silent = source;

      console.debug("[Audio] Silence initialized");
    },
    /**
     * Stops the entire sound system and cleans it up
     */
    purgeAudioSystem() {
      if (this._nodeCache.silent) {
        this._nodeCache.silent.stop();
        this._nodeCache.silent.disconnect();
        this._nodeCache.silent = null;
        console.debug("[Audio] Silence purged");
      }

      if (this._nodeCache.gain) {
        this._nodeCache.gain.disconnect();
        this._nodeCache.gain = null;
        console.debug("[Audio] Gain purged");
      }

      if (this.audioContext) {
        this.audioContext.suspend();
        this.audioContext.close();
        this.audioContext = null;
        console.debug("[Audio] Audio context purged");
      }

      for (const key in this._bufferCache) {
        delete this._bufferCache[key];
      }
    },
  },
});
